import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback
} from 'react'
import { sendGetRequest, sendPostRequest, ENDPOINTS } from '@/services/api'
import { LOCAL_KEYS } from '@/services/browserStorage'
import { useAuth } from '@/contexts/AuthContext'

interface OnboardingData {
  completed: boolean
  step?: number
  data?: {
    healthcare_area?: string
    primary_interest?: string[]
    career_stage?: string
    academic_term?: string
    goal?: string
    learning_preferences?: string[]
    weekly_time_available?: string
  }
}

interface OnboardingFormData {
  healthcare_area: string | null
  healthcare_area_custom?: string | null
  primary_interest: string[]
  career_stage: string | null
  academic_term: string | null
  goal: string | null
  learning_preferences: string[]
  weekly_time_available: string | null
}

interface OnboardingContextType {
  onboardingData: OnboardingData | null
  loading: boolean
  error: string | null
  shouldShowOnboarding: boolean
  refreshOnboardingData: () => Promise<void>
  saveOnboardingData: (data: OnboardingData['data']) => Promise<void>
  skipOnboarding: () => void
  formData: OnboardingFormData
  currentStep: number
  updateFormData: (updates: Partial<OnboardingFormData>) => void
  setCurrentStep: (step: number) => void
  resetFormData: () => void
  loadExistingData: () => void
  isEditModalOpen: boolean
  openEditModal: () => void
  closeEditModal: () => void
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined
)

export function OnboardingProvider({
  children
}: {
  children: React.ReactNode
}) {
  const { signed, loadingUser, user } = useAuth()

  // Função para gerar chave do localStorage específica do usuário
  const getUserSpecificKey = (baseKey: string) => {
    return user.idDB ? `${baseKey}_${user.idDB}` : baseKey
  }
  const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(
    null
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [formData, setFormData] = useState<OnboardingFormData>({
    healthcare_area: null,
    healthcare_area_custom: null,
    primary_interest: [],
    career_stage: null,
    academic_term: null,
    goal: null,
    learning_preferences: [],
    weekly_time_available: null
  })
  const [currentStep, setCurrentStep] = useState(1)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const handleSetCurrentStep = (step: number) => {
    setCurrentStep(step)
  }

  const openEditModal = () => {
    setIsEditModalOpen(true)
  }

  const closeEditModal = () => {
    setIsEditModalOpen(false)
  }

  const fetchOnboardingData = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await sendGetRequest(ENDPOINTS.get_user_onboarding)
      const data = response?.data

      const isNotFound =
        data?.status === 'fail' &&
        typeof data?.msg === 'string' &&
        data.msg.includes('Cod: 404-')

      if (response.status === 404 || isNotFound) {
        setOnboardingData({ completed: false })
      } else if (
        data &&
        data.status !== 'fail' &&
        Object.keys(data).length > 0
      ) {
        setOnboardingData({ completed: true, data })
      } else {
        setOnboardingData({ completed: false })
      }
    } catch (err: any) {
      console.error(' Erro na requisição:', err)

      const data = err.response?.data
      const isNotFound =
        data?.status === 'fail' &&
        typeof data?.msg === 'string' &&
        data.msg.includes('Cod: 404-')

      if (err.response?.status === 404 || isNotFound) {
        setOnboardingData({ completed: false })
        setError(null)
      } else {
        console.warn('Erro na API, não mostrando wizard:', err.message)
        setOnboardingData({ completed: false })
        setError(err.message || 'Erro ao carregar dados')
      }
    } finally {
      setLoading(false)
    }
  }

  const refreshOnboardingData = async () => {
    await fetchOnboardingData()
  }

  const skipOnboarding = () => {
    const userSpecificKey = getUserSpecificKey(LOCAL_KEYS.onboarding_skipped)
    localStorage.setItem(userSpecificKey, Date.now().toString())
    setOnboardingData({ completed: true })
    resetFormData()
  }

  const updateFormData = (updates: Partial<OnboardingFormData>) => {
    setFormData(prev => {
      const newData = { ...prev, ...updates }
      return newData
    })
  }

  const resetFormData = () => {
    setFormData({
      healthcare_area: null,
      healthcare_area_custom: null,
      primary_interest: [],
      career_stage: null,
      academic_term: null,
      goal: null,
      learning_preferences: [],
      weekly_time_available: null
    })
    setCurrentStep(1)
  }

  const loadExistingData = useCallback(() => {
    const actualData =
      (onboardingData?.data as { data?: OnboardingData['data'] })?.data ||
      onboardingData?.data

    if (actualData) {
      const standardAreas = [
        'medicine',
        'nursing',
        'pharmacy',
        'physiotherapy',
        'nutrition',
        'biomedicine',
        'dentistry',
        'directions_run',
        'others'
      ]
      const healthcareArea = actualData.healthcare_area || null
      const isCustomArea =
        healthcareArea && !standardAreas.includes(healthcareArea)

      setFormData({
        healthcare_area: isCustomArea ? 'others' : healthcareArea,
        healthcare_area_custom: isCustomArea ? healthcareArea : null,
        primary_interest: actualData.primary_interest || [],
        career_stage: actualData.career_stage || null,
        academic_term: actualData.academic_term || null,
        goal: actualData.goal || null,
        learning_preferences: actualData.learning_preferences || [],
        weekly_time_available: actualData.weekly_time_available || null
      })

      setCurrentStep(1)
    }
  }, [onboardingData])

  const saveOnboardingData = async (data: OnboardingData['data']) => {
    try {
      setLoading(true)
      setError(null)
      await sendPostRequest(ENDPOINTS.save_user_onboarding, data)

      // Remove o flag de onboarding ignorado quando salva com sucesso
      const userSpecificKey = getUserSpecificKey(LOCAL_KEYS.onboarding_skipped)
      localStorage.removeItem(userSpecificKey)
      setOnboardingData({ completed: true, data })
      setCurrentStep(1)
      await refreshOnboardingData()
    } catch (err: unknown) {
      console.error('Error saving onboarding data:', err)
      setError((err as Error)?.message || 'Erro ao salvar dados do onboarding')
      throw err
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!loadingUser && signed) {
      const timeoutId = setTimeout(() => {
        fetchOnboardingData()
      }, 100)

      return () => clearTimeout(timeoutId)
    } else if (!loadingUser && !signed) {
      setOnboardingData({ completed: false })
      setLoading(false)
    }
  }, [loadingUser, signed])

  const skippedAt = localStorage.getItem(
    getUserSpecificKey(LOCAL_KEYS.onboarding_skipped)
  )

  const SKIP_TTL = 1000 * 60 * 60 * 24 * 7

  const shouldShowOnboarding =
    !loading &&
    onboardingData?.completed === false &&
    (!skippedAt || Date.now() - Number(skippedAt) > SKIP_TTL) &&
    (user.accessType === 'b2b' || user.accessType === 'sso')

  return (
    <OnboardingContext.Provider
      value={{
        onboardingData,
        loading,
        error,
        shouldShowOnboarding,
        refreshOnboardingData,
        saveOnboardingData,
        skipOnboarding,
        formData,
        currentStep,
        updateFormData,
        setCurrentStep: handleSetCurrentStep,
        resetFormData,
        loadExistingData,
        isEditModalOpen,
        openEditModal,
        closeEditModal
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}

export function useOnboarding() {
  const context = useContext(OnboardingContext)
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider')
  }
  return context
}
